import React, { useState } from "react";
import { Link } from "react-router-dom";

const LANGS = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
];

const CONTENT = {
    en: {
        kicker: "An Invitation",
        title: "Why We Pray",
        lead: "Prayer is not the recital of words. It is being on terms of friendship with God — the King of all creation — who waits, delights, and speaks to the soul that turns toward Him.",
        sections: [
            {
                heading: "Because He is worth it",
                body: "God is not distant. He is not indifferent. He is closer to us than our own heartbeat, and His deepest desire is our joy. To pray is to acknowledge the goodness of the One who made us, and to open a door He has been knocking on all along.",
            },
            {
                heading: "Because we were made for it",
                body: "The human heart is restless until it rests in God, wrote Saint Augustine. Every longing — for beauty, for peace, for love — is at its root a longing for Him. Prayer answers what the soul was designed to do.",
            },
            {
                heading: "Because prayer changes us",
                body: "We do not pray to inform God of things He already knows. We pray because prayer forms us — sculpts humility, patience, hope, and love into a heart that would otherwise grow cold. The Rosary in particular is a school of meditation, walking us through the mysteries of Christ with Mary's hand in ours.",
            },
            {
                heading: "Because the Church has always prayed",
                body: "For two thousand years, saints and sinners, kings and shepherds, the sick, the joyful, the dying — all have spoken to God in the same words we speak today. When you pray the Our Father, you pray with the whole Communion of Saints. You are never praying alone.",
            },
        ],
        outro: "Begin here. In Latin, English, or Spanish — spoken aloud, whispered, or held in silence. He is already listening.",
    },
    es: {
        kicker: "Una Invitación",
        title: "Por qué Oramos",
        lead: "La oración no es la recitación de palabras. Es estar en términos de amistad con Dios — el Rey de toda la creación — quien espera, se deleita y habla al alma que se vuelve hacia Él.",
        sections: [
            {
                heading: "Porque Él lo merece",
                body: "Dios no está lejano. No es indiferente. Está más cerca de nosotros que nuestro propio latido, y su deseo más profundo es nuestra alegría. Orar es reconocer la bondad de Aquel que nos hizo y abrir una puerta a la que Él ha estado llamando desde siempre.",
            },
            {
                heading: "Porque fuimos hechos para ello",
                body: "El corazón humano está inquieto hasta que descansa en Dios, escribió San Agustín. Todo anhelo — de belleza, de paz, de amor — es en su raíz un anhelo de Él. La oración responde a aquello para lo que el alma fue creada.",
            },
            {
                heading: "Porque la oración nos transforma",
                body: "No oramos para informar a Dios de cosas que ya sabe. Oramos porque la oración nos forma — esculpe humildad, paciencia, esperanza y amor en un corazón que de otro modo se enfriaría. El Rosario en particular es una escuela de meditación, guiándonos por los misterios de Cristo de la mano de María.",
            },
            {
                heading: "Porque la Iglesia siempre ha orado",
                body: "Durante dos mil años, santos y pecadores, reyes y pastores, los enfermos, los alegres, los moribundos — todos han hablado a Dios con las mismas palabras que decimos hoy. Cuando rezas el Padre Nuestro, oras con toda la Comunión de los Santos. Nunca oras solo.",
            },
        ],
        outro: "Empieza aquí. En latín, inglés o español — en voz alta, en susurros o en silencio. Él ya está escuchando.",
    },
};

const WhyPray = () => {
    const [lang, setLang] = useState("en");
    const c = CONTENT[lang];

    return (
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="why-pray-page">
            <div className="flex justify-center gap-1 mb-10">
                {LANGS.map(l => (
                    <button key={l.code} onClick={() => setLang(l.code)} data-testid={`why-lang-${l.code}`}
                        className={`px-3 py-1.5 text-xs small-caps border ${lang===l.code ? 'border-liturgy accent-liturgy' : 'border-border text-muted-foreground hover:accent-liturgy'}`}>
                        {l.label}
                    </button>
                ))}
            </div>

            <div className="text-center mb-14">
                <div className="small-caps text-xs text-muted-foreground mb-3">{c.kicker}</div>
                <h1 className="font-serif text-4xl md:text-5xl leading-tight">{c.title}</h1>
                <p className="mt-6 max-w-xl mx-auto text-lg text-muted-foreground italic leading-relaxed">{c.lead}</p>
            </div>

            <div className="space-y-12">
                {c.sections.map((s, i) => (
                    <section key={i}>
                        <h2 className="font-serif text-2xl mb-3 accent-liturgy">{s.heading}</h2>
                        <p className="text-base leading-loose text-foreground/90">{s.body}</p>
                    </section>
                ))}
            </div>

            <div className="mt-16 text-center border-t border-border pt-10">
                <p className="italic text-muted-foreground leading-relaxed max-w-xl mx-auto">{c.outro}</p>
                <div className="mt-6 flex gap-3 justify-center flex-wrap">
                    <Link to="/how-to-pray" className="px-5 py-2 border border-liturgy accent-liturgy text-xs small-caps hover:bg-liturgy hover:text-white transition-colors" data-testid="link-how">
                        {lang === "es" ? "Cómo Orar" : "How to Pray"}
                    </Link>
                    <Link to="/what-to-pray" className="px-5 py-2 border border-liturgy accent-liturgy text-xs small-caps hover:bg-liturgy hover:text-white transition-colors" data-testid="link-what">
                        {lang === "es" ? "Qué Orar" : "What to Pray"}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default WhyPray;
